import { useQuery } from '@tanstack/react-query'
import { ApiError, get } from '../../api'
import { useSession } from '../../session'
import { Chip, Icon } from '../../ui'
import { can, day, hm, utc, type AdminUserOverview, type AdminUserUsage, type UsageStatement } from './model'

/*
 * The Statements tab of a user's card (BILL-REL, B3a PR5; deferred by BILL-B6a until statements
 * landed): GET /api/admin/users/{id}/usage — one row per quota and usage period, newest first.
 * A statement opens when the user goes on air in a period and closes 1 h after it ends, with the
 * quantity counted once and an HMAC signature (docs/billing/metering.md). MetricsTable .compact,
 * states as chip + word + icon (StateSystem), empty state per EmptyStates. Console: EN only.
 * The signature itself is a hex string the API returns; it is not a secret, but it says nothing to
 * a person, so only whether it checks is shown.
 */

const QUOTA: Record<UsageStatement['key'], string> = { stream_hours_month: 'Stream hours', captions_hours_month: 'Captions' }
const KIND: Record<UsageStatement['period_kind'], string> = { subscription: 'Subscription', trial: 'Trial', calendar: 'Calendar month' }

/** The statement's state: open (counting), signed and checks, closed unsigned, or a signature that does not check. */
export function statementState(s: UsageStatement): { state: 'start' | 'live' | 'idle' | 'fail'; word: string; title: string } {
  if (!s.closed_at) return { state: 'start', word: 'OPEN', title: 'Counted when the period ends + 1 h' }
  if (!s.signature) return { state: 'idle', word: 'UNSIGNED', title: 'Closed without a signature: the center had no USAGE_STATEMENT_KEY' }
  if (s.signature_valid === false) return { state: 'fail', word: 'DOES NOT CHECK', title: 'The stored signature does not match the statement: it was changed after it closed' }
  if (s.signature_valid === undefined) return { state: 'idle', word: 'SIGNED', title: 'Signed; the center has no key to check it now' }
  return { state: 'live', word: 'SIGNED', title: 'Signed with HMAC-SHA256; the signature checks' }
}

export function Statements({ u }: { u: AdminUserOverview }) {
  const { user } = useSession()
  const right = can(user, 'billing.read')
  const q = useQuery({
    queryKey: ['admin-user-usage', u.id],
    queryFn: () => get<AdminUserUsage>(`/api/admin/users/${u.id}/usage`),
    enabled: right,
    retry: false,
  })
  if (!right) return <p className="text-ui-body-sm text-muted">Statements need the billing.read permission.</p>
  if (q.error) {
    const off = q.error instanceof ApiError && q.error.status === 409
    return (
      <p role={off ? undefined : 'alert'} className={`text-ui-body-sm ${off ? 'text-muted' : 'text-fail'}`}>
        {off ? 'Billing is off on this center (BILLING_MODE=off): no statements are kept.' : `Statements did not load: ${(q.error as Error).message}`}
      </p>
    )
  }
  if (!q.data) return <p className="text-ui-body-sm text-muted">Loading statements…</p>
  const { statements, signed } = q.data
  return (
    <div className="flex flex-col gap-3" data-testid="statements">
      <div className="flex flex-wrap items-center gap-2 text-ui-body-sm" data-testid="statements-signing">
        {signed ? (
          <>
            <Chip state="live" size="sm" still>SIGNING ON</Chip>
            <span className="text-text-soft">Closed statements are signed with HMAC-SHA256. A changed statement stops checking.</span>
          </>
        ) : (
          <>
            <Chip state="warn" size="sm">SIGNING OFF</Chip>
            <span className="text-text-soft">USAGE_STATEMENT_KEY is not set on the center: statements close unsigned. Set it once — docs/billing/rollout.md §5.1.</span>
          </>
        )}
      </div>
      {statements.length === 0 ? (
        <div className="sx-empty">
          <div className="ic"><Icon name="plan" size={24} /></div>
          <h4>No statements yet</h4>
          <p>A statement opens when this user goes on air in a period and closes 1 h after the period ends.</p>
        </div>
      ) : (
        <div className="sx-panel overflow-x-auto !p-0">
          <table className="sx-table compact" data-testid="statements-table">
            <thead>
              <tr>
                <th scope="col">Period (UTC)</th>
                <th scope="col">Kind</th>
                <th scope="col">Plan</th>
                <th scope="col">Quota</th>
                <th scope="col" className="num">Used</th>
                <th scope="col" className="num">Limit</th>
                <th scope="col">Closed (UTC)</th>
                <th scope="col">Statement</th>
              </tr>
            </thead>
            <tbody>
              {statements.map((s) => {
                const st = statementState(s)
                const late = s.late_quantity != null && s.late_quantity !== s.quantity
                return (
                  <tr key={`${s.key}|${s.period_start}`} data-testid="statement-row" data-state={st.state}>
                    <td className="sx-mono">{`${day(s.period_start)} – ${day(s.period_end)}`}</td>
                    <td>{KIND[s.period_kind] ?? s.period_kind}</td>
                    <td className="sx-mono">{s.plan_id || '—'}</td>
                    <td>{QUOTA[s.key] ?? s.key}</td>
                    <td className="num tabular">
                      {s.closed_at ? hm(s.quantity) : <span className="text-muted">—</span>}
                      {late && (
                        <span className="block text-ui-caption text-warn" title="Sessions that arrived after the statement closed; the statement is not changed (owner decision B3-4)" data-testid="statement-late">
                          late: {hm(s.late_quantity!)}
                        </span>
                      )}
                    </td>
                    <td className="num tabular">{s.limit == null ? <span className="text-muted">No limit</span> : hm(s.limit)}</td>
                    <td className="sx-mono">{s.closed_at ? utc(s.closed_at).replace(' UTC', '') : <span className="text-muted">—</span>}</td>
                    <td>
                      <Chip state={st.state} size="sm" still title={st.title}>{st.word}</Chip>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          <div className="border-t border-line px-3 py-2 text-ui-caption text-muted">
            An open statement is counted when its period ends + 1 h. The current count is on the Usage tab and in the list.
          </div>
        </div>
      )}
    </div>
  )
}
